<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps } from 'vue';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import GenericTable2 from '@/components/tables/GenericTable2.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const sourceNotFound = computed(() =>
  props.sourceErrors?.[props.widget.source]?.type === 'not_found'
    ? props.widget.source : null
);

// State
const tableData = ref([]);
const loading = ref(true);
let unsubscribe = null;

// Lifecycle
onMounted(() => {
  fetchData();
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});

// Methods
const fetchData = () => {
  if (!props.widget.source || !props.widget.columns || props.widget.columns.length === 0) {
    loading.value = false;
    return;
  }

  const subcollectionRef = collection(db, 'data', props.dataID, props.widget.source);
  const rowLimit = props.widget.rowLimit || 10;
  const q = query(subcollectionRef, orderBy('messageUTC', 'desc'), limit(rowLimit));
  
  unsubscribe = onSnapshot(q, (snapshot) => {
    const data = [];
    
    snapshot.forEach((doc) => {
      const docData = doc.data();
      const row = {};
      
      // Build row based on configured columns
      props.widget.columns.forEach(column => {
        if (column.field) {
          const label = column.label || column.field;
          row[label] = docData[column.field] !== undefined ? docData[column.field] : '--';
        }
      });
      
      data.push(row);
    });
    
    tableData.value = data;
    loading.value = false;
  });
};
</script>

<template>
  <div class="flex flex-col h-full bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm">
    <div class="h-[3px] bg-mc-red shrink-0"></div>
    <h3
      v-if="widget.title"
      class="font-display font-extrabold text-[15px] leading-tight text-mc-ink dark:text-on-dark px-4 pt-3 pb-2 border-b border-mc-steel dark:border-line-on-dark shrink-0 m-0"
    >
      {{ widget.title }}
    </h3>
    <div v-if="sourceNotFound" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <p class="font-display font-extrabold text-2xl text-ink-muted dark:text-on-dark-muted">N/A</p>
        <p class="font-mono text-[10px] uppercase tracking-[0.12em] text-mc-red mt-1">Source "{{ sourceNotFound }}" not found</p>
      </div>
    </div>
    <div v-else class="flex-1 min-h-0">
      <GenericTable2
        :data="tableData"
        :loading="loading"
        :pagination="true"
        :rowsPerPage="widget.rowLimit || 10"
      />
    </div>
  </div>
</template>
